import React, { useCallback, useEffect, useState } from 'react';
import AdminProductForm from './AdminProductForm';
import * as api from '../api/productsApi';
import { useNotification } from '../context/NotificationContext';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification();

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listProducts();
      setProducts(list || []);
    } catch (e) {
      console.error('Failed to load products', e);
      addNotification('Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => { reload(); }, [reload]);

  const handleAdd = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleEdit = (p) => {
    setEditing(p);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.deleteProduct(id);
      await reload();
      addNotification('Product deleted', 'success');
    } catch (e) {
      console.error('Delete failed', e);
      addNotification('Failed to delete product', 'error');
    }
  };

  const handleSave = async (payload) => {
    try {
      const isUpdate = products.find(p => String(p.id) === String(payload.id));
      if (isUpdate) {
        await api.updateProduct(payload);
        addNotification('Product updated', 'success');
      } else {
        await api.createProduct(payload);
        addNotification('Product created', 'success');
      }
      await reload();
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      console.error('Save failed', e);
      addNotification('Failed to save product', 'error');
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Products</h5>
        <div>
          <button className="btn btn-sm btn-success" onClick={handleAdd}>Add Product</button>
        </div>
      </div>

      {showForm && (
        <div className="card card-body mb-3">
          <AdminProductForm initial={editing || {}} onCancel={() => { setShowForm(false); setEditing(null); }} onSave={handleSave} />
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-sm">
          <thead>
            <tr>
              <th style={{ width: 80 }}>Image</th>
              <th>Name</th>
              <th style={{ width: 120 }}>Price</th>
              <th style={{ width: 160 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className="text-center">Loading…</td>
              </tr>
            )}
            {!loading && products.map(p => (
              <tr key={p.id}>
                <td>
                  {p.image ? (<img src={p.image} alt="thumb" style={{ width: 64, height: 64, objectFit: 'cover' }} />) : <div style={{ width: 64, height: 64, background: '#eee' }} />}
                </td>
                <td>
                  <strong>{p.name}</strong>
                  <div className="text-muted small">{p.description && p.description.slice(0, 80)}</div>
                </td>
                <td>${p.price}</td>
                <td>
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(p)}>Edit</button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(p.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && products.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-muted">No products yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminProducts;
